import { useState, useMemo, useCallback } from 'react';
import { Sidebar, useSidebar } from '@/components/Sidebar';
import { Link } from 'react-router-dom';
import {
  Plus,
  Search,
  TrendingUp,
  TrendingDown,
  DollarSign,
  CreditCard,
  ArrowLeftRight,
  Calendar,
  X,
  Upload,
  Download,
  MoreHorizontal,
  Settings,
  Loader2,
} from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { CustomSelect } from '@/components/ui/custom-select';
import {
  CategoryMultiSelect,
  type CategoryGroupOption,
} from '@/components/ui/category-multi-select';
import { useTransactions } from '@/hooks/useTransactions';
import { useAccounts } from '@/hooks/useAccounts';
import { usePowensPendingConnections } from '@/hooks/usePowensPendingConnections';
import { useCategories } from '@/hooks/useCategories';
import { useTransactionFilters } from '@/hooks/useTransactionFilters';
import { useCurrency } from '@/contexts';
import { useExchangeRates } from '@/hooks/useExchangeRates';
import { useExplorationAchievement } from '@/hooks/useAchievementTriggers';
import { PageHeader } from '@/components/PageHeader';
import { GroupedTransactionList } from '@/components/TransactionItem';
import { TransactionModal } from '@/components/TransactionModal';
import { TransferModal } from '@/components/TransferModal';
import {
  ImportTransactionsModal,
  type TransactionImportRow,
} from '@/components/ImportTransactionsModal';
import { exportStyledExcel } from '@/lib/exportExcel';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type { Database } from '@/lib/database.types';

type Transaction = Database['public']['Tables']['transactions']['Row'] & {
  category?: Database['public']['Tables']['categories']['Row'] | null;
  account?: Database['public']['Tables']['accounts']['Row'] | null;
  to_account?: Database['public']['Tables']['accounts']['Row'] | null;
};

export default function TransactionsPage() {
  const { collapsed: sidebarCollapsed } = useSidebar();
  useExplorationAchievement('explore_transactions');
  const { t, language } = useTranslation();
  const confirm = useConfirm();
  const { formatAmount, currency } = useCurrency();
  const { getRate } = useExchangeRates();
  const { user } = useAuth();
  const { transactions, loading, loadTransactions, deleteTransaction } = useTransactions();
  const { activeAccounts, loadAccounts } = useAccounts();
  const { pendingConnections: powensPending, oldestImportedFrom: powensImportedFrom } =
    usePowensPendingConnections(activeAccounts);
  const { categories, incomeGroups, expenseGroups, getCategoryIcon } = useCategories();
  const {
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    clearDateRange,
    filteredTransactions,
  } = useTransactionFilters(transactions);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [isImportOpen, setIsImportOpen] = useState(false);
  const [isActionsOpen, setIsActionsOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | undefined>();
  const [selectedAccountFilter, setSelectedAccountFilter] = useState<string>('all');
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);

  // Build category filter options grouped by type then by group
  const categoryGroupOptions = useMemo((): CategoryGroupOption[] => {
    const buildGroups = (groups: typeof incomeGroups, type: 'income' | 'expense') =>
      groups
        .filter((g) => !g.is_hidden)
        .map((g) => ({
          label: g.name,
          type,
          categories: g.categories
            .filter((c) => !c.is_hidden)
            .map((c) => ({
              value: c.id,
              label: c.name,
              icon: getCategoryIcon(c.icon || 'default'),
              type,
            })),
        }))
        .filter((g) => g.categories.length > 0);
    return [...buildGroups(incomeGroups, 'income'), ...buildGroups(expenseGroups, 'expense')];
  }, [incomeGroups, expenseGroups, getCategoryIcon]);

  // Filter by account and categories
  const accountFilteredTransactions = useMemo(() => {
    let result = filteredTransactions;
    if (selectedAccountFilter !== 'all') {
      result = result.filter(
        (t) => t.account_id === selectedAccountFilter || t.to_account_id === selectedAccountFilter
      );
    }
    if (selectedCategoryIds.length > 0) {
      result = result.filter((t) => t.category_id && selectedCategoryIds.includes(t.category_id));
    }
    return result;
  }, [filteredTransactions, selectedAccountFilter, selectedCategoryIds]);

  // Sync type filter buttons with category filter
  const handleFilterChange = useCallback(
    (type: 'all' | 'income' | 'expense' | 'transfer') => {
      setFilter(type);
      // When clicking income/expense type buttons, also update category selection
      if (type === 'income') {
        const incomeIds = categories
          .filter((c) => c.type === 'income' && !c.is_hidden)
          .map((c) => c.id);
        setSelectedCategoryIds(incomeIds);
      } else if (type === 'expense') {
        const expenseIds = categories
          .filter((c) => c.type === 'expense' && !c.is_hidden)
          .map((c) => c.id);
        setSelectedCategoryIds(expenseIds);
      } else {
        setSelectedCategoryIds([]);
      }
    },
    [setFilter, categories]
  );

  // Dynamic KPI stats based on all active filters
  const filteredStats = useMemo(() => {
    // Pending operations are listed but excluded from every total: the bank can
    // still change the amount or drop the operation entirely.
    const settled = accountFilteredTransactions.filter((t) => !t.is_pending);
    const income = settled
      .filter((t) => t.type === 'income')
      .reduce((sum, t) => {
        const acctCurrency = (t as any).account?.currency || 'EUR';
        const rate = getRate(acctCurrency, currency) ?? 1;
        return sum + t.amount * rate;
      }, 0);
    const expense = settled
      .filter((t) => t.type === 'expense')
      .reduce((sum, t) => {
        const acctCurrency = (t as any).account?.currency || 'EUR';
        const rate = getRate(acctCurrency, currency) ?? 1;
        return sum + t.amount * rate;
      }, 0);
    const transfers = settled
      .filter((t) => t.type === 'transfer')
      .reduce((sum, t) => {
        const acctCurrency = (t as any).account?.currency || 'EUR';
        const rate = getRate(acctCurrency, currency) ?? 1;
        return sum + t.amount * rate;
      }, 0);
    return { income, expense, transfers, balance: income - expense };
  }, [accountFilteredTransactions, currency, getRate]);

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      description: t('transactions.delete.confirm'),
      variant: 'destructive',
    });
    if (!ok) return;

    const result = await deleteTransaction(id);
    if (result.success) {
      await loadAccounts();
    } else {
      alert('Error deleting transaction');
    }
  };

  const handleEdit = (transaction: Transaction) => {
    setSelectedTransaction(transaction);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTransaction(undefined);
  };

  const handleModalSuccess = async () => {
    await loadTransactions();
    await loadAccounts();
  };

  const handleImportTransactions = useCallback(
    async (rows: TransactionImportRow[]) => {
      if (!user) return { success: false, imported: 0, errors: ['Not authenticated'] };

      const insertData = rows.map((row) => ({
        amount: row.amount,
        description: row.description,
        date: row.date,
        type: row.type,
        category_id: row.category_id,
        account_id: row.account_id,
        user_id: user.id,
      }));

      const { error } = await supabase.from('transactions').insert(insertData);

      if (error) return { success: false, imported: 0, errors: [error.message] };

      await loadTransactions();
      await loadAccounts();
      return { success: true, imported: rows.length, errors: [] };
    },
    [user, loadTransactions, loadAccounts]
  );

  const handleExportExcel = useCallback(async () => {
    const isFr = language === 'fr';
    await exportStyledExcel({
      sheetName: 'Transactions',
      fileName: 'clevia-transactions.xlsx',
      columns: [
        { header: 'Date', key: 'date', width: 14, style: 'date' },
        { header: 'Description', key: 'description', width: 30 },
        { header: isFr ? 'Montant' : 'Amount', key: 'amount', width: 16, style: 'currency' },
        { header: 'Type', key: 'type', width: 14 },
        { header: isFr ? 'Catégorie' : 'Category', key: 'category', width: 18 },
        { header: isFr ? 'Compte' : 'Account', key: 'account', width: 20 },
      ],
      data: accountFilteredTransactions.map((t) => {
        const d = new Date(t.date);
        return {
          date: d.toLocaleDateString(isFr ? 'fr-FR' : 'en-US', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          }),
          description: t.description || '',
          amount: t.amount,
          type:
            t.type === 'income'
              ? isFr
                ? 'Revenu'
                : 'Income'
              : t.type === 'expense'
                ? isFr
                  ? 'Dépense'
                  : 'Expense'
                : isFr
                  ? 'Virement'
                  : 'Transfer',
          category: t.category?.name || '',
          account: t.account?.name || '',
        };
      }),
      totals: {
        amount: accountFilteredTransactions
          .filter((t) => !t.is_recurring)
          .reduce((sum, t) => sum + (t.type === 'income' ? t.amount : -t.amount), 0),
      },
    });
  }, [accountFilteredTransactions, language]);

  return (
    <div className="min-h-screen bg-[#f9fafb] dark:bg-[#0f1117]">
      <Sidebar activePage="transactions" />
      <main className={`${sidebarCollapsed ? 'lg:ml-[72px]' : 'lg:ml-64'}`}>
        <PageHeader
          title={t('transactions.title')}
          subtitle={t('transactions.subtitle')}
          action={
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setIsActionsOpen(!isActionsOpen)}
                  className="flex items-center justify-center h-[42px] w-[42px] bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </button>
                {isActionsOpen && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setIsActionsOpen(false)} />
                    <div className="absolute right-0 top-full mt-2 z-40 w-48 bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700/50 rounded-xl shadow-lg overflow-hidden">
                      <button
                        onClick={() => {
                          handleExportExcel();
                          setIsActionsOpen(false);
                        }}
                        disabled={accountFilteredTransactions.length === 0}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
                      >
                        <Upload className="w-4 h-4" />
                        {t('transactions.export') || 'Exporter'}
                      </button>
                      <button
                        onClick={() => {
                          setIsImportOpen(true);
                          setIsActionsOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                      >
                        <Download className="w-4 h-4" />
                        {t('transactions.import') || 'Importer'}
                      </button>
                    </div>
                  </>
                )}
              </div>
              <button
                onClick={() => setIsTransferModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
              >
                <ArrowLeftRight className="w-4 h-4" />
                {t('transactions.transfer')}
              </button>
              <button
                id="guide-transactions"
                onClick={() => {
                  setSelectedTransaction(undefined);
                  setIsModalOpen(true);
                }}
                className="flex items-center gap-2 px-5 py-2.5 bg-[#16c47f] hover:bg-[#12b070] text-white font-semibold rounded-xl transition-all duration-200 shadow-sm hover:shadow-md scroll-mt-24 target:ring-4 target:ring-[#16c47f]/30 target:ring-offset-4"
              >
                <Plus className="w-4 h-4" />
                {t('transactions.addNew')}
              </button>
            </div>
          }
        />

        <div className="p-6 space-y-4">
          {/* Sticky Filter Bar */}
          <div className="sticky top-0 z-20 -mx-6 px-6 pt-2 pb-3 bg-[#f9fafb]/95 dark:bg-[#0f1117]/95 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 p-5">
              <div className="flex flex-col gap-4">
                {/* Row 1: Search, Account, Date Range */}
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                    <input
                      type="text"
                      placeholder={t('transactions.search')}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-12 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] transition-all"
                    />
                  </div>

                  {/* Account Filter */}
                  <div className="relative min-w-[180px]">
                    <CustomSelect
                      value={selectedAccountFilter}
                      onChange={(value) => setSelectedAccountFilter(value)}
                      options={[
                        { value: 'all', label: t('transactions.filter.allAccounts') },
                        ...activeAccounts.map((account) => ({
                          value: account.id,
                          label: account.name,
                        })),
                      ]}
                    />
                  </div>

                  {/* Category Filter */}
                  <div className="relative min-w-[180px]">
                    <CategoryMultiSelect
                      selectedIds={selectedCategoryIds}
                      onChange={setSelectedCategoryIds}
                      groups={categoryGroupOptions}
                      placeholder={t('transactions.filter.allCategories')}
                      incomeLabel={t('transactions.filter.income')}
                      expenseLabel={t('transactions.filter.expense')}
                      countLabel={(n) =>
                        t('transactions.filter.nCategories').replace('{n}', String(n))
                      }
                    />
                  </div>

                  {/* Date Range Filter */}
                  <div className="flex items-center gap-2">
                    <div className="relative">
                      <DatePicker
                        selected={dateRange.startDate}
                        onChange={(date) =>
                          setDateRange({
                            ...dateRange,
                            startDate: date,
                          })
                        }
                        placeholderText={t('transactions.filter.from')}
                        dateFormat="dd/MM/yyyy"
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] text-sm"
                        wrapperClassName="w-full"
                      />
                      <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
                    </div>
                    <span className="text-gray-400 dark:text-gray-500">→</span>
                    <div className="relative">
                      <DatePicker
                        selected={dateRange.endDate}
                        onChange={(date) =>
                          setDateRange({
                            ...dateRange,
                            endDate: date,
                          })
                        }
                        placeholderText={t('transactions.filter.to')}
                        dateFormat="dd/MM/yyyy"
                        className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#16c47f]/30 focus:border-[#16c47f] text-sm"
                        wrapperClassName="w-full"
                      />
                      <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 pointer-events-none" />
                    </div>
                    {(dateRange.startDate || dateRange.endDate) && (
                      <button
                        onClick={clearDateRange}
                        className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-500 dark:text-gray-400 transition-colors"
                        title={t('transactions.filter.clearDates')}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Row 2: Type Filters + Categories Link */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleFilterChange('all')}
                    className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      filter === 'all'
                        ? 'bg-[#16c47f] text-white font-semibold shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('transactions.filter.all')}
                  </button>
                  <button
                    onClick={() => handleFilterChange('income')}
                    className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      filter === 'income'
                        ? 'bg-green-500 text-white font-semibold shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('transactions.filter.income')}
                  </button>
                  <button
                    onClick={() => handleFilterChange('expense')}
                    className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      filter === 'expense'
                        ? 'bg-[#78909C] text-white font-semibold shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('transactions.filter.expense')}
                  </button>
                  <button
                    onClick={() => handleFilterChange('transfer')}
                    className={`px-5 py-2.5 rounded-xl font-medium text-sm transition-all ${
                      filter === 'transfer'
                        ? 'bg-blue-500 text-white font-semibold shadow-sm'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t('transactions.filter.transfer')}
                  </button>

                  <div className="flex-1 flex justify-end">
                    <Link
                      to="/settings?tab=categories"
                      className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-[#16c47f] dark:hover:text-[#16c47f] hover:bg-[#16c47f]/10 rounded-xl transition-all"
                    >
                      <Settings className="w-4 h-4" />
                      {t('transactions.customizeCategories')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Compact KPI Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-emerald-500 rounded-lg flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('transactions.stats.income')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatAmount(filteredStats.income)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-[#78909C] to-[#607D8B] rounded-lg flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('transactions.stats.expense')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatAmount(filteredStats.expense)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                  <ArrowLeftRight className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('transactions.stats.transfers')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatAmount(filteredStats.transfers)}
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700/50 px-4 py-3">
              <div className="flex items-center gap-2.5">
                <div
                  className={`w-8 h-8 bg-gradient-to-br ${filteredStats.balance >= 0 ? 'from-emerald-500 to-teal-500' : 'from-rose-500 to-red-500'} rounded-lg flex items-center justify-center`}
                >
                  <DollarSign className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {t('transactions.stats.balance')}
                  </p>
                  <p className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatAmount(filteredStats.balance)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Transaction List */}
          <div>
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block w-10 h-10 border-4 border-[#16c47f] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : accountFilteredTransactions.length === 0 && powensPending.length > 0 ? (
              // A bank is linked but Powens has not handed its data over yet.
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 text-center py-12">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-sky-400 to-blue-600 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-7 h-7 text-white animate-spin" />
                </div>
                <p className="text-gray-900 dark:text-white font-semibold mb-1">
                  {t('powens.importing')}
                </p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  {t('powens.importingDesc')}
                </p>
              </div>
            ) : accountFilteredTransactions.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700/50 text-center py-12">
                <div className="w-14 h-14 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-7 h-7 text-gray-400 dark:text-gray-500" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-1">{t('transactions.empty')}</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">
                  {t('transactions.emptyDesc')}
                </p>

                {/* An aggregated account with nothing in it is usually not a
                    failure: nothing before the connection date is ever
                    imported. Saying so beats letting the user wait for history
                    that is never coming. */}
                {powensImportedFrom && (
                  <p className="mt-4 text-gray-400 dark:text-gray-500 text-xs max-w-sm mx-auto">
                    {t('powens.noHistoryNote')}{' '}
                    {new Date(powensImportedFrom).toLocaleDateString(
                      language === 'fr' ? 'fr-FR' : 'en-GB',
                      { day: 'numeric', month: 'long', year: 'numeric' }
                    )}
                    .
                  </p>
                )}
              </div>
            ) : (
              <GroupedTransactionList
                transactions={accountFilteredTransactions}
                onEdit={handleEdit}
                onDelete={handleDelete}
                highlightAccountId={
                  selectedAccountFilter !== 'all' ? selectedAccountFilter : undefined
                }
                language={language}
              />
            )}
          </div>
        </div>

        <TransactionModal
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onSuccess={handleModalSuccess}
          transaction={selectedTransaction}
          transactionHistory={transactions}
        />

        <TransferModal
          isOpen={isTransferModalOpen}
          onClose={() => setIsTransferModalOpen(false)}
          onSuccess={handleModalSuccess}
        />

        <ImportTransactionsModal
          isOpen={isImportOpen}
          onClose={() => setIsImportOpen(false)}
          onImport={handleImportTransactions}
          accounts={activeAccounts.map((a) => ({ id: a.id, name: a.name }))}
          categories={categories.map((c) => ({
            id: c.id,
            name: c.name,
            type: c.type as 'income' | 'expense',
          }))}
        />
      </main>
    </div>
  );
}
